import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#updatePairControlState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.addEventListener('click', this.#handlePairControl);
    this.addEventListener('submit', this.#syncPairReturnTo);

    const pairTrack = this.querySelector('[data-yx-cart-pair-track]');
    pairTrack?.addEventListener('scroll', this.#updatePairControlState, { passive: true });
    this.#updatePairControlState();

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updatePairControlState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener('click', this.#handlePairControl);
    this.removeEventListener('submit', this.#syncPairReturnTo);

    const pairTrack = this.querySelector('[data-yx-cart-pair-track]');
    pairTrack?.removeEventListener('scroll', this.#updatePairControlState);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  #handlePairControl = (event) => {
    const control = event.target instanceof Element ? event.target.closest('[data-yx-cart-pair-direction]') : null;
    if (!(control instanceof HTMLButtonElement) || !this.contains(control)) return;

    const pairTrack = this.querySelector('[data-yx-cart-pair-track]');
    if (!(pairTrack instanceof HTMLElement)) return;

    const firstCard = pairTrack.querySelector('.yx-cart-drawer__pair-card');
    const gap = parseFloat(getComputedStyle(pairTrack).gap) || 0;
    const step = (firstCard?.getBoundingClientRect().width || pairTrack.clientWidth) + gap;
    const direction = control.dataset.yxCartPairDirection === 'previous' ? -1 : 1;

    pairTrack.scrollBy({ left: direction * step, behavior: 'smooth' });
    window.setTimeout(() => this.#updatePairControlState(), 250);
  };

  #syncPairReturnTo = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('.yx-cart-drawer__pair-add-form')) return;

    const returnTo = form.querySelector('[data-yx-pair-return-to]');
    if (returnTo instanceof HTMLInputElement) returnTo.value = `${window.location.pathname}${window.location.search}`;
  };

  #updatePairControlState = () => {
    const pairTrack = this.querySelector('[data-yx-cart-pair-track]');
    if (!(pairTrack instanceof HTMLElement)) return;

    const previous = this.querySelector('[data-yx-cart-pair-direction="previous"]');
    const next = this.querySelector('[data-yx-cart-pair-direction="next"]');
    const maxScrollLeft = pairTrack.scrollWidth - pairTrack.clientWidth;

    if (previous instanceof HTMLButtonElement) previous.disabled = pairTrack.scrollLeft <= 1;
    if (next instanceof HTMLButtonElement) next.disabled = maxScrollLeft <= 1 || pairTrack.scrollLeft >= maxScrollLeft - 1;
  };

  open(event) {
    event?.preventDefault();
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('[data-cart-drawer-content], .cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
